"use client";

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  TrendingUp,
  Users,
  Calculator,
  Cake,
  Zap,
} from "lucide-react";
import { POZICIA_LABELS } from "@/lib/utils";
import type {
  KandidatSHracom,
  StatistikyZostavy,
  ZoznamNarodenin,
} from "@/types/database";

interface HracInfoProps {
  kandidat: KandidatSHracom;
  slot: number;
}

export function HracInfo({ kandidat, slot }: HracInfoProps) {
  const { hrac } = kandidat;

  const vek = useMemo(() => {
    if (!hrac.datum_narodenia) return null;
    const narodenie = new Date(hrac.datum_narodenia);
    const dnes = new Date();
    let vek = dnes.getFullYear() - narodenie.getFullYear();
    const mesiacovyRozdiel = dnes.getMonth() - narodenie.getMonth();
    if (
      mesiacovyRozdiel < 0 ||
      (mesiacovyRozdiel === 0 && dnes.getDate() < narodenie.getDate())
    ) {
      vek--;
    }
    return vek;
  }, [hrac.datum_narodenia]);

  const formatDatum = (datum: string | null) => {
    if (!datum) return "—";
    return new Date(datum).toLocaleDateString("sk-SK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatPrijem = (prijem: number) => {
    return new Intl.NumberFormat("sk-SK", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(prijem);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-muted/50 rounded-lg gap-4">
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className="w-8 shrink-0 justify-center">
          {slot}
        </Badge>
        <div className="space-y-1 min-w-0">
          <p className="font-medium truncate">{hrac.cele_meno}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-muted-foreground">
            <span className="truncate">{hrac.klub || "Bez klubu"}</span>
            {hrac.sutaz && <span className="hidden sm:inline">•</span>}
            {hrac.sutaz && <span className="truncate">{hrac.sutaz}</span>}
            {hrac.strana_hokejky && (
              <Badge variant="outline" className="text-xs w-fit">
                {hrac.strana_hokejky === "L" ? "Ľavá" : "Pravá"} hokejka
              </Badge>
            )}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
            {vek && (
              <span className="flex items-center gap-1">
                <Cake className="h-3 w-3" />
                {vek} rokov
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDatum(hrac.datum_narodenia)}
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {hrac.prijem !== null ? formatPrijem(hrac.prijem) : "—"}
            </span>
            <span className="flex items-center gap-1">
              <Calculator className="h-3 w-3" />
              Hodnota:{" "}
              {hrac.hodnota_hraca !== null
                ? formatPrijem(hrac.hodnota_hraca)
                : "—"}
            </span>
            <span className="flex items-center gap-1 font-bold text-primary">
              <Zap className="h-3 w-3" />
              {hrac.sezona_zapasy}z ({hrac.sezona_goly}+{hrac.sezona_asistencie}
              ) {hrac.sezona_body}b
            </span>
          </div>
        </div>
      </div>
      {kandidat.stav_zdravia !== "nezname" && (
        <Badge variant="outline" className="text-xs w-fit">
          {kandidat.stav_zdravia}
        </Badge>
      )}
    </div>
  );
}

interface StatistikyZostavyProps {
  statistiky: StatistikyZostavy | null;
  narodeniny: ZoznamNarodenin[];
}

export function StatistikyZostavy({
  statistiky,
  narodeniny,
}: StatistikyZostavyProps) {
  if (!statistiky) return null;

  const formatPrijem = (suma: number) => {
    return new Intl.NumberFormat("sk-SK", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(suma);
  };

  const formatVek = (vek: number | null) => {
    return vek ? `${vek} rokov` : "—";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Štatistiky zostavy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-status-info/10 rounded-lg">
              <div className="text-2xl font-bold text-status-info">
                {formatVek(Math.round(statistiky.priemerny_vek))}
              </div>
              <div className="text-sm text-muted-foreground">Priemerný vek</div>
            </div>
            <div className="text-center p-4 bg-status-success/10 rounded-lg">
              <div className="text-2xl font-bold text-status-success">
                {formatPrijem(statistiky.suma_prijmov)}
              </div>
              <div className="text-sm text-muted-foreground">
                Celkový príjem
              </div>
            </div>
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {formatPrijem(statistiky.suma_hodnota_hracov)}
              </div>
              <div className="text-sm text-muted-foreground">Hodnota tímu</div>
            </div>
            <div className="text-center p-4 bg-status-warning/10 rounded-lg">
              <div className="text-2xl font-bold text-status-warning">
                {statistiky.pocet_hracov}
              </div>
              <div className="text-sm text-muted-foreground">Počet hráčov</div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="text-center p-3 bg-muted/30 rounded-lg border border-muted/40">
              <div className="text-xl font-bold">{statistiky.suma_zapasy}</div>
              <div className="text-xs uppercase font-bold text-muted-foreground">
                Zápasy spolu
              </div>
            </div>
            <div className="text-center p-3 bg-status-success/5 rounded-lg border border-status-success/10">
              <div className="text-xl font-bold text-status-success">
                {statistiky.suma_goly}
              </div>
              <div className="text-xs uppercase font-bold text-muted-foreground">
                Góly spolu
              </div>
            </div>
            <div className="text-center p-3 bg-status-info/5 rounded-lg border border-status-info/10">
              <div className="text-xl font-bold text-status-info">
                {statistiky.suma_asistencie}
              </div>
              <div className="text-xs uppercase font-bold text-muted-foreground">
                Asistencie spolu
              </div>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-lg border border-primary/10">
              <div className="text-xl font-bold text-primary">
                {statistiky.suma_body}
              </div>
              <div className="text-xs uppercase font-bold text-muted-foreground">
                Body spolu
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {statistiky.najmladsi_hrac_meno && (
          <Card className="border-status-success/20 bg-status-success/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-status-success">
                <Users className="h-5 w-5" />
                Najmladší hráč
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="font-medium text-foreground">
                  {statistiky.najmladsi_hrac_meno}
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 text-xs">
                  <Badge
                    variant="outline"
                    className="border-status-success/30 text-status-success w-fit"
                  >
                    {formatVek(statistiky.najmladsi_hrac_vek)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {statistiky.najstarsi_hrac_meno && (
          <Card className="border-status-info/20 bg-status-info/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-status-info">
                <Users className="h-5 w-5" />
                Najstarší hráč
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="font-medium text-foreground">
                  {statistiky.najstarsi_hrac_meno}
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 text-xs">
                  <Badge
                    variant="outline"
                    className="border-status-info/30 text-status-info w-fit"
                  >
                    {formatVek(statistiky.najstarsi_hrac_vek)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {narodeniny.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Kalendár narodenín
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {narodeniny.map((hrac) => (
                <div
                  key={hrac.hrac_id}
                  className="flex items-center justify-between p-2 bg-muted/30 rounded"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-medium">{hrac.cele_meno}</div>
                    <Badge variant="outline" className="text-xs">
                      {POZICIA_LABELS[hrac.pozicia]}
                    </Badge>
                    {hrac.strana_hokejky && (
                      <Badge variant="outline" className="text-xs">
                        {hrac.strana_hokejky === "L" ? "Ľavá" : "Pravá"}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>
                      {hrac.datum_narodenia &&
                        new Date(hrac.datum_narodenia).toLocaleDateString(
                          "sk-SK",
                          {
                            month: "short",
                            day: "numeric",
                          }
                        )}
                    </span>
                    <span>({formatVek(hrac.vek_k_ms)})</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
